import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { CanvasSectionBlock } from '../sections/sectionRegistry';
import type { EnquiryFormData } from '../../types';
import { useFlowContext } from './FlowProvider';
import { REQUEST_FLOW_FIELD_IDS } from '../../lib/flowFieldExtensions';
import { asRequestItemList } from '../../lib/flowNodeListHelpers';
import { requestItemsToSelectedVases, resolveRequestItemsFieldId } from '../../lib/flowNodeRuntime';
import RequestItemsPanel from './RequestItemsPanel';
import { findFlowNodeIdByType, removeRequestItemFromFlow } from './requestItemFlowActions';
import { submitEnquiryRequest } from '../../lib/submitEnquiry';
import { resolveFlowForBlock } from '../../lib/flowBlockResolution';
import { resolveFlowNodeSockets } from '../../lib/flowNodeDefinitions';
import { resolveRequestFormContent } from '../../lib/requestFormContent';

type RequestFormSurfaceProps = {
  block: CanvasSectionBlock;
  sectionId?: string;
  enquiryEmailTo?: string;
  onEnquirySubmit?: (formData: EnquiryFormData) => void;
  isAdmin?: boolean;
  isInteractPreview?: boolean;
  selectionOverlay?: React.ReactNode;
};

function getInteractionAllowed(isAdmin: boolean, isInteractPreview: boolean) {
  return !isAdmin || isInteractPreview;
}

function getInteractionProps(allowInteraction: boolean) {
  if (!allowInteraction) return {};
  return {
    'data-builder-allow-interaction': 'true' as const,
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      event.stopPropagation();
    },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
    },
  };
}

export const RequestFormSurface: React.FC<RequestFormSurfaceProps> = React.memo(({
  block,
  sectionId,
  enquiryEmailTo = 'info@eco3dcor.com',
  onEnquirySubmit,
  isAdmin = false,
  isInteractPreview = false,
  selectionOverlay,
}) => {
  const { flows, getFlowValue, patchFlowValues, invokeFlowNode } = useFlowContext();
  const activeFlow = useMemo(
    () => resolveFlowForBlock(flows, { block, sectionId }),
    [block, flows, sectionId]
  );
  const flowId = activeFlow?.id ?? null;
  const requestItemsFieldId = useMemo(
    () =>
      resolveRequestItemsFieldId(activeFlow, {
        nodeBlockId: block.id,
      }) ?? REQUEST_FLOW_FIELD_IDS.requestItems,
    [activeFlow, block.id]
  );
  const requestItems = useMemo(
    () => (flowId ? asRequestItemList(getFlowValue(flowId, requestItemsFieldId)) : []),
    [flowId, getFlowValue, requestItemsFieldId]
  );
  const requestFormNode = useMemo(
    () => activeFlow?.nodes.find((node) => node.blockType === 'request-form' && node.blockId === block.id) ?? null,
    [activeFlow, block.id]
  );
  const removedItemOutputFieldId = useMemo(
    () => requestFormNode?.outputs.find((binding) => binding.socketId === 'removedItemId')?.fieldId ?? null,
    [requestFormNode]
  );
  const removedItemOutputConnected = useMemo(
    () =>
      Boolean(
        activeFlow &&
          requestFormNode &&
          removedItemOutputFieldId &&
          activeFlow.nodes.some((candidate) => {
            if (candidate.id === requestFormNode.id) return false;
            return candidate.inputs.some((binding) => binding.fieldId === removedItemOutputFieldId);
          })
      ),
    [activeFlow, removedItemOutputFieldId, requestFormNode]
  );
  const submitSuccessOutputFieldId = useMemo(
    () => requestFormNode?.outputs.find((binding) => binding.socketId === 'submitSuccess')?.fieldId ?? null,
    [requestFormNode]
  );
  const selectedVases = useMemo(() => requestItemsToSelectedVases(requestItems), [requestItems]);
  const removeNodeId = useMemo(
    () => findFlowNodeIdByType(activeFlow, 'remove-from-list'),
    [activeFlow]
  );
  const clearNodeId = useMemo(
    () => findFlowNodeIdByType(activeFlow, 'clear-list'),
    [activeFlow]
  );
  const allowInteraction = getInteractionAllowed(isAdmin, isInteractPreview);
  const interactionProps = getInteractionProps(allowInteraction);
  const blockContent =
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : undefined;

  const [formData, setFormData] = useState<EnquiryFormData>({
    name: '',
    email: '',
    selectedVases: [],
    colourFinish: 'shifting',
    favouriteColours: [],
    comments: '',
    newsletterOptIn: false,
  });
  const [errors, setErrors] = useState({ name: '', email: '' });
  const [honeypot, setHoneypot] = useState('');
  const [formStartedAt] = useState<number>(() => Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState<string | null>(null);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  const contentStrings = resolveRequestFormContent(blockContent);
  const footerMessage = contentStrings.footerText.replace('{emailTo}', enquiryEmailTo);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;
    const nextValue =
      type === 'checkbox' && 'checked' in event.target
        ? (event.target as HTMLInputElement).checked
        : value;
    setFormData((current) => ({
      ...current,
      [name]: nextValue,
    }));
    if (name === 'name' || name === 'email') {
      setErrors((current) => ({ ...current, [name]: '' }));
    }
  };

  const validate = () => {
    let valid = true;
    const nextErrors = { name: '', email: '' };
    if (!formData.name.trim()) {
      nextErrors.name = 'Name is required';
      valid = false;
    }
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      nextErrors.email = 'Please enter a valid email address';
      valid = false;
    }
    setErrors(nextErrors);
    return valid;
  };

  const handleRemoveItem = (itemId: string) => {
    const item = requestItems.find((entry) => entry.id === itemId);
    if (!item) return;
    if (flowId && removedItemOutputFieldId && removedItemOutputConnected) {
      patchFlowValues(flowId, { [removedItemOutputFieldId]: null });
      patchFlowValues(flowId, { [removedItemOutputFieldId]: item.id });
      return;
    }
    if (flowId && removeNodeId && activeFlow) {
      removeRequestItemFromFlow({
        flow: activeFlow,
        flowId,
        item,
        removeNodeId,
        patchFlowValues,
        invokeFlowNode,
      });
      return;
    }
    if (!flowId) return;
    patchFlowValues(flowId, {
      [requestItemsFieldId]: requestItems.filter((entry) => entry.id !== itemId),
      [REQUEST_FLOW_FIELD_IDS.currentRequestItem]: null,
    });
  };

  const emitSubmitSuccess = () => {
    if (!flowId || !activeFlow || !submitSuccessOutputFieldId) return;
    const pulseValue = Date.now();
    patchFlowValues(flowId, { [submitSuccessOutputFieldId]: pulseValue });
    activeFlow.nodes
      .filter((candidate) => candidate.id !== requestFormNode?.id)
      .filter((candidate) =>
        candidate.inputs.some((binding) => {
          if (binding.fieldId !== submitSuccessOutputFieldId) return false;
          const socketDefinition = resolveFlowNodeSockets(candidate, activeFlow).find(
            (socket) => socket.direction === 'input' && socket.id === binding.socketId
          );
          return socketDefinition?.portKind === 'trigger';
        })
      )
      .forEach((candidate) => {
        invokeFlowNode(flowId, candidate.id);
      });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!allowInteraction) return;
    setSubmitErrorMessage(null);
    setSubmitSuccessMessage(null);
    if (!validate()) return;
    if (requestItems.length === 0) {
      toast.error('Please add at least one request item first.');
      setSubmitErrorMessage('Please add at least one request item before submitting.');
      return;
    }

    const submittedSelectedVases = selectedVases;
    const submittedFavouriteColours = Array.from(
      new Set(requestItems.flatMap((item) => item.colours))
    );
    const submittedFormData: EnquiryFormData = {
      ...formData,
      selectedVases: submittedSelectedVases,
      favouriteColours: submittedFavouriteColours,
    };

    try {
      setIsSubmitting(true);
      await submitEnquiryRequest({
        formData: submittedFormData,
        requestItems,
        honeypot,
        formStartedAt,
      });
      toast.success('Enquiry submitted successfully!');
      setSubmitSuccessMessage('Your enquiry has been sent successfully.');
      setFormData({
        name: '',
        email: '',
        selectedVases: [],
        colourFinish: 'shifting',
        favouriteColours: [],
        comments: '',
        newsletterOptIn: false,
      });
      setErrors({ name: '', email: '' });
      setHoneypot('');
      onEnquirySubmit?.(submittedFormData);
      if (flowId && requestFormNode) {
        invokeFlowNode(flowId, requestFormNode.id);
      } else if (flowId) {
        patchFlowValues(flowId, {
          [requestItemsFieldId]: [],
          [REQUEST_FLOW_FIELD_IDS.currentRequestItem]: null,
        });
      }
      emitSubmitSuccess();
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit enquiry. Please try again.');
      setSubmitErrorMessage('We could not submit your enquiry right now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-[24px] border border-stone-200 bg-white shadow-sm"
      {...interactionProps}
    >
      <div className="border-b border-stone-200/80 px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
          Request Form
        </div>
        <h3 className="text-sm font-semibold text-stone-900">{contentStrings.title}</h3>
        <p className="mt-1 text-xs text-stone-500">{contentStrings.description}</p>
      </div>

      <div className="h-[calc(100%-78px)] overflow-y-auto p-3">
        <RequestItemsPanel
          items={requestItems}
          canRemove={Boolean(allowInteraction && flowId && requestItems.length > 0)}
          canClear={Boolean(allowInteraction && requestItems.length > 0 && flowId)}
          showRemoveButton
          showClearButton={requestItems.length > 0}
          emptyMessage={contentStrings.emptyMessage}
          onRemoveItem={handleRemoveItem}
          onClear={() => {
            if (!flowId) return;
            if (clearNodeId) {
              invokeFlowNode(flowId, clearNodeId);
              return;
            }
            patchFlowValues(flowId, {
              [requestItemsFieldId]: [],
              [REQUEST_FLOW_FIELD_IDS.currentRequestItem]: null,
            });
          }}
        />

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <input
            type="text"
            name="company"
            value={honeypot}
            onChange={(event) => setHoneypot(event.target.value)}
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
          />

          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">
              {contentStrings.nameLabel}
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={contentStrings.namePlaceholder}
              disabled={!allowInteraction || isSubmitting}
              className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 outline-none transition focus:border-stone-500 disabled:cursor-not-allowed disabled:bg-stone-100"
            />
            {errors.name ? <div className="text-xs text-red-600">{errors.name}</div> : null}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">
              {contentStrings.emailLabel}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={contentStrings.emailPlaceholder}
              disabled={!allowInteraction || isSubmitting}
              className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 outline-none transition focus:border-stone-500 disabled:cursor-not-allowed disabled:bg-stone-100"
            />
            {errors.email ? <div className="text-xs text-red-600">{errors.email}</div> : null}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">
              {contentStrings.commentsLabel}
            </label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              placeholder={contentStrings.commentsPlaceholder}
              disabled={!allowInteraction || isSubmitting}
              rows={4}
              className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 outline-none transition focus:border-stone-500 disabled:cursor-not-allowed disabled:bg-stone-100"
            />
          </div>

          <label className="flex items-start gap-2 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600">
            <input
              type="checkbox"
              name="newsletterOptIn"
              checked={Boolean(formData.newsletterOptIn)}
              onChange={handleChange}
              disabled={!allowInteraction || isSubmitting}
              className="mt-0.5"
            />
            <span>
              {contentStrings.newsletterLabel}
            </span>
          </label>

          {!allowInteraction ? (
            <div className="text-center text-xs text-stone-500">
              Visible to visitors. Switch to Interact to test the form.
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!allowInteraction || isSubmitting || requestItems.length === 0}
            className={`w-full rounded-xl px-4 py-3 text-sm font-semibold transition ${
              !allowInteraction || isSubmitting || requestItems.length === 0
                ? 'cursor-not-allowed border border-stone-300 bg-stone-200 text-stone-500'
                : 'bg-stone-900 text-white hover:bg-stone-800'
            }`}
          >
            {isSubmitting ? contentStrings.submittingLabel : contentStrings.submitLabel}
          </button>
          <div className="text-center text-xs text-stone-500">{footerMessage}</div>
          {submitSuccessMessage ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {submitSuccessMessage}
            </div>
          ) : null}
          {submitErrorMessage ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {submitErrorMessage}
            </div>
          ) : null}
        </form>
      </div>
      {selectionOverlay}
    </div>
  );
});

export default RequestFormSurface;
